import React, { useState, useCallback, useRef } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, ListSubheader } from '@mui/material';
import { toPng } from 'html-to-image';
import { trackStatsConfigChange } from '../utils/analytics';
import { trackFbStatsConfigChange } from '../utils/facebookPixel';
import { trackPinStatsConfigChange } from '../utils/pinterestTag';
import { PALETTE } from '../theme';
import { GEOGRAPHY_STROKE, GEOGRAPHY_STROKE_WIDTH } from './mapGeographyStyles';

// ============================================================================
// CATALOG
// ============================================================================

export const STATS_POSITIONS = [
    { value: 'hidden', label: 'Hidden' },
    { value: 'above',  label: 'Top' },
    { value: 'below',  label: 'Bottom' },
];

export const STATS_STYLE_GROUPS = [
    {
        label: 'Text',
        styles: [
            { value: 'bold',         label: 'Solid' },
            { value: 'outline',      label: 'Outline' },
        ],
    },
    {
        label: 'Progress bar',
        styles: [
            { value: 'bar-top',      label: 'Label on top' },
            { value: 'bar-inside',   label: 'Label inside' },
        ],
    },
    {
        label: 'Number',
        styles: [
            { value: 'big-number',   label: 'Big percent' },
            { value: 'big-fraction', label: 'Big fraction' },
        ],
    },
    {
        label: 'Chart',
        styles: [
            { value: 'donut',        label: 'Donut' },
            { value: 'gauge',        label: 'Gauge' },
        ],
    },
    {
        label: 'One per region',
        styles: [
            { value: 'pips',         label: 'Circles' },
            { value: 'pips-squares', label: 'Squares' },
        ],
    },
    {
        label: 'Stamp',
        styles: [
            { value: 'stamp',        label: 'Rectangle' },
            { value: 'stamp-round',  label: 'Round seal' },
            { value: 'stamp-banner', label: 'Banner' },
        ],
    },
];

const FLAT_STYLES = STATS_STYLE_GROUPS.flatMap(g => g.styles);
const STYLE_LABEL = Object.fromEntries(FLAT_STYLES.map(s => [s.value, s.label]));

export const formatStatsText = (visited, total, label) => {
    const pct = total > 0 ? Math.round((visited / total) * 100) : 0;
    return `${visited}/${total} ${label} · ${pct}%`;
};

// ============================================================================
// PALETTE — print-friendly: no shadows, no opacity, no translucency
// ============================================================================

const TEXT_DARK = PALETTE.text;
const SURFACE = '#ffffff';
const TRACK = '#e0e0e0';
const SUBTEXT = '#5b6c7e';
const DEFAULT_ACCENT = PALETTE.primary;

// ============================================================================
// RENDERERS
// Each renderer takes (visited, total, label, pct, accent) and the SAME
// "selection color" (accent) drives every coloured surface in every style.
// ============================================================================

const BoldStats = ({ visited, total, label, accent }) => (
    <Box sx={{
        display: 'inline-block',
        backgroundColor: accent || DEFAULT_ACCENT,
        color: SURFACE,
        px: 2.25, py: 1.1,
        borderRadius: 2,
        fontWeight: 800,
        fontSize: '1.15rem',
        lineHeight: 1.3,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
    }}>{visited} / {total} {label}</Box>
);

const OutlineStats = ({ text, accent }) => (
    <Box sx={{
        display: 'inline-block',
        backgroundColor: SURFACE,
        color: TEXT_DARK,
        border: `2.5px solid ${accent || DEFAULT_ACCENT}`,
        px: 1.75, py: 0.85,
        borderRadius: 2,
        fontWeight: 700,
        fontSize: '1.05rem',
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
    }}>{text}</Box>
);

// no %, label centered
const BarTopStats = ({ visited, total, label, pct, accent }) => (
    <Box sx={{ width: 360, display: 'flex', flexDirection: 'column', gap: 0.6, alignItems: 'center' }}>
        <Box sx={{ fontWeight: 700, fontSize: '1rem', color: TEXT_DARK, letterSpacing: 0.2 }}>
            {visited} / {total} {label}
        </Box>
        <Box sx={{ width: '100%', height: 14, borderRadius: 7, backgroundColor: TRACK, overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', backgroundColor: accent || DEFAULT_ACCENT }} />
        </Box>
    </Box>
);

// no %, label drawn twice (dark over the empty track, white clipped to the fill)
const BarInsideStats = ({ visited, total, label, pct, accent }) => {
    const W = 380, H = 34;
    const text = `${visited} / ${total} ${label}`;
    const textSx = {
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: '0.95rem',
        letterSpacing: 0.3, whiteSpace: 'nowrap',
    };
    return (
        <Box sx={{
            position: 'relative',
            width: W, height: H,
            backgroundColor: TRACK,
            borderRadius: H / 2,
            overflow: 'hidden',
        }}>
            <Box sx={{
                position: 'absolute', top: 0, left: 0, bottom: 0,
                width: `${pct}%`, backgroundColor: accent || DEFAULT_ACCENT,
            }} />
            <Box sx={{ ...textSx, color: TEXT_DARK }}>{text}</Box>
            <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${pct}%`, overflow: 'hidden' }}>
                <Box sx={{ ...textSx, width: W, color: SURFACE }}>{text}</Box>
            </Box>
        </Box>
    );
};

const BigNumberStats = ({ visited, total, label, pct, accent }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
        <Box sx={{ fontSize: '3rem', fontWeight: 900, color: accent || DEFAULT_ACCENT, letterSpacing: -1 }}>
            {pct}%
        </Box>
        <Box sx={{ fontSize: '0.7rem', fontWeight: 800, color: SUBTEXT, textTransform: 'uppercase', letterSpacing: 2, mt: 0.5 }}>
            {visited} / {total} {label}
        </Box>
    </Box>
);

const BigFractionStats = ({ visited, total, label, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Box sx={{ fontSize: '2.5rem', fontWeight: 900, color: accent || DEFAULT_ACCENT, letterSpacing: -1, lineHeight: 1 }}>
            {visited}
            <Box component="span" sx={{ color: SUBTEXT, fontSize: '1.5rem', mx: 0.5 }}>/</Box>
            {total}
        </Box>
        <Box sx={{ fontSize: '0.85rem', fontWeight: 700, color: SUBTEXT, textTransform: 'uppercase', letterSpacing: 1.5 }}>
            {label}
        </Box>
    </Box>
);

const DonutStats = ({ visited, total, label, pct, accent }) => {
    const r = 44, stroke = 12;
    const size = (r + stroke) * 2;
    const circumference = 2 * Math.PI * r;
    const offset = circumference * (1 - pct / 100);
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <svg width={size} height={size} style={{ display: 'block' }}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
                <circle
                    cx={size / 2} cy={size / 2} r={r}
                    fill="none" stroke={accent || DEFAULT_ACCENT} strokeWidth={stroke}
                    strokeLinecap="butt"
                    strokeDasharray={circumference} strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central"
                    fontSize="20" fontWeight="900" fill={TEXT_DARK}>{pct}%</text>
            </svg>
            <Box sx={{ fontSize: '1.05rem', fontWeight: 800, color: TEXT_DARK, lineHeight: 1.3 }}>
                <Box>{visited}/{total}</Box>
                <Box component="span" sx={{ fontSize: '0.8rem', color: SUBTEXT, fontWeight: 700 }}>{label}</Box>
            </Box>
        </Box>
    );
};

const GaugeStats = ({ visited, total, label, pct, accent }) => {
    const r = 56, stroke = 14;
    const w = r * 2 + stroke * 2;       // 140 (chord 2r + half-stroke each side)
    const h = r + stroke;               // 70 (radius + full stroke for cap room)
    const cy = r + stroke / 2;          // chord sits near the bottom of the SVG
    const arc = Math.PI * r;
    const offset = arc * (1 - pct / 100);
    // sweep-flag = 0 → arc bows UPWARD (toward smaller y), like a speedometer
    const d = `M ${stroke} ${cy} A ${r} ${r} 0 0 0 ${w - stroke} ${cy}`;
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
            <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
                <path d={d} stroke={TRACK} strokeWidth={stroke} fill="none" strokeLinecap="butt" />
                <path
                    d={d}
                    stroke={accent || DEFAULT_ACCENT} strokeWidth={stroke} fill="none" strokeLinecap="butt"
                    strokeDasharray={arc} strokeDashoffset={offset}
                />
                <text x={w / 2} y={cy - 14} textAnchor="middle"
                    fontSize="26" fontWeight="900" fill={TEXT_DARK}>{pct}%</text>
            </svg>
            <Box sx={{ fontSize: '0.85rem', fontWeight: 700, color: SUBTEXT, mt: 0.5, letterSpacing: 0.5 }}>
                {visited}/{total} {label}
            </Box>
        </Box>
    );
};

const buildPips = ({ visited, total, accent, shape }) => {
    const MAX_PIPS = 50;
    let pips, filled;
    if (total <= MAX_PIPS) {
        pips = total;
        filled = visited;
    } else {
        pips = MAX_PIPS;
        filled = Math.round((visited / total) * MAX_PIPS);
    }
    const color = accent || DEFAULT_ACCENT;
    const radius = shape === 'square' ? '2px' : '50%';
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 460, justifyContent: 'center' }}>
            {Array.from({ length: pips }).map((_, i) => (
                <Box key={i} sx={{
                    width: 12, height: 12,
                    borderRadius: radius,
                    backgroundColor: i < filled ? color : SURFACE,
                    border: `2px solid ${color}`,
                    boxSizing: 'border-box',
                }} />
            ))}
        </Box>
    );
};

const PipsStats = ({ visited, total, label, accent }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        {buildPips({ visited, total, accent, shape: 'circle' })}
        <Box sx={{ fontSize: '0.95rem', fontWeight: 800, color: TEXT_DARK, letterSpacing: 0.3 }}>
            {visited} / {total} {label}
        </Box>
    </Box>
);

const PipsSquaresStats = ({ visited, total, label, accent }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
        {buildPips({ visited, total, accent, shape: 'square' })}
        <Box sx={{ fontSize: '0.95rem', fontWeight: 800, color: TEXT_DARK, letterSpacing: 0.3 }}>
            {visited} / {total} {label}
        </Box>
    </Box>
);

const StampStats = ({ visited, total, label, accent }) => {
    const color = accent || DEFAULT_ACCENT;
    return (
        <Box sx={{
            display: 'inline-block',
            backgroundColor: SURFACE,
            border: `3px solid ${color}`,
            color,
            px: 2, py: 0.4,
            transform: 'rotate(-3deg)',
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: 2,
            borderRadius: 1,
            textAlign: 'center',
            lineHeight: 1.15,
        }}>
            <Box sx={{ fontSize: '1.3rem' }}>Visited</Box>
            <Box sx={{ fontSize: '0.95rem', mt: '-2px' }}>{visited}/{total}</Box>
            <Box sx={{ fontSize: '0.7rem', mt: '-2px', letterSpacing: 1.5 }}>{label}</Box>
        </Box>
    );
};

const StampRoundStats = ({ visited, total, label, accent }) => {
    const color = accent || DEFAULT_ACCENT;
    const size = 110;
    return (
        <Box sx={{
            width: size, height: size,
            borderRadius: '50%',
            border: `3px solid ${color}`,
            color,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: 1.5,
            backgroundColor: SURFACE,
            textAlign: 'center',
            lineHeight: 1.1,
            position: 'relative',
        }}>
            {/* inner ring */}
            <Box sx={{
                position: 'absolute', inset: 6,
                borderRadius: '50%',
                border: `1.5px solid ${color}`,
                pointerEvents: 'none',
            }} />
            <Box sx={{ fontSize: '0.9rem' }}>Visited</Box>
            <Box sx={{ fontSize: '1.4rem', my: '-2px' }}>{visited}/{total}</Box>
            <Box sx={{ fontSize: '0.55rem', letterSpacing: 1 }}>{label}</Box>
        </Box>
    );
};

const StampBannerStats = ({ visited, total, label, accent }) => {
    const color = accent || DEFAULT_ACCENT;
    return (
        <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1.5,
            backgroundColor: SURFACE,
            border: `3px double ${color}`,
            color,
            px: 2.5, py: 0.75,
            fontFamily: '"Courier New", Courier, monospace',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: 2,
        }}>
            <Box sx={{ fontSize: '1.15rem' }}>Visited</Box>
            <Box sx={{ width: 1, height: 22, backgroundColor: color }} />
            <Box sx={{ fontSize: '1.15rem' }}>{visited}/{total} {label}</Box>
        </Box>
    );
};

const STYLE_RENDERERS = {
    'bold':         BoldStats,
    'outline':      OutlineStats,
    'bar-top':      BarTopStats,
    'bar-inside':   BarInsideStats,
    'big-number':   BigNumberStats,
    'big-fraction': BigFractionStats,
    'donut':        DonutStats,
    'gauge':        GaugeStats,
    'pips':         PipsStats,
    'pips-squares': PipsSquaresStats,
    'stamp':        StampStats,
    'stamp-round':  StampRoundStats,
    'stamp-banner': StampBannerStats,
};

const StatsPill = ({ style = 'bold', accent, visited, total, label }) => {
    const text = formatStatsText(visited, total, label);
    const pct = total > 0 ? Math.round((visited / total) * 100) : 0;
    const Renderer = STYLE_RENDERERS[style] || BoldStats;
    return <Renderer text={text} accent={accent} visited={visited} total={total} label={label} pct={pct} />;
};

// ============================================================================
// HOOK
// ============================================================================

export const useMapStatsConfig = ({ mapType, label, defaults = {} }) => {
    const [config, setConfigState] = useState({
        position: 'above',
        style: 'bold',
        label,
        ...defaults,
    });

    const setConfig = useCallback((next) => {
        setConfigState((prev) => {
            const merged = typeof next === 'function' ? next(prev) : { ...prev, ...next };
            if (merged.position !== prev.position) {
                trackStatsConfigChange(mapType, merged.position);
                trackFbStatsConfigChange(mapType, merged.position);
                trackPinStatsConfigChange(mapType, merged.position);
            }
            if (merged.style !== prev.style) {
                trackStatsConfigChange(mapType, `style:${merged.style}`);
                trackFbStatsConfigChange(mapType, `style:${merged.style}`);
                trackPinStatsConfigChange(mapType, `style:${merged.style}`);
            }
            return merged;
        });
    }, [mapType]);

    return [config, setConfig];
};

// ============================================================================
// CONTROLS — with live previews in the dropdown
// ============================================================================

const POSITION_DROPDOWN_WIDTH = 130;
const STYLE_DROPDOWN_WIDTH = 230;

const SAMPLE = { visited: 27, total: 50, label: 'states' };

// Compact preview thumbnail. Scaled with CSS transform so we don't have
// to maintain two sets of dimensions per renderer.
const StatsPreview = ({ style, accent, scale = 0.5, width = 200, height = 38 }) => (
    <Box sx={{
        width, height,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexShrink: 0,
    }}>
        <Box sx={{
            transform: `scale(${scale})`,
            transformOrigin: 'left center',
            display: 'inline-block',
        }}>
            <StatsPill style={style} accent={accent} {...SAMPLE} />
        </Box>
    </Box>
);

const accentBorderSx = (accent) => accent ? {
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accent },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accent },
} : {};

export const MapStatsControls = ({ config, onChange, accent }) => {
    // Render trigger: just the style label (no scaled preview — too tall for an
    // input row). The preview lives inside the menu below.
    // flexWrap lets the two dropdowns stack on narrow / mobile screens
    // instead of overflowing the toolbar.
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', width: { xs: '100%', sm: 'auto' } }}>
            <FormControl size="small" sx={{ width: { xs: '100%', sm: POSITION_DROPDOWN_WIDTH } }}>
                <InputLabel id="stats-position-label">Stats</InputLabel>
                <Select
                    labelId="stats-position-label"
                    value={config.position}
                    label="Stats"
                    onChange={(e) => onChange({ position: e.target.value })}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        ...accentBorderSx(accent),
                    }}
                >
                    {STATS_POSITIONS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl
                size="small"
                sx={{ width: { xs: '100%', sm: STYLE_DROPDOWN_WIDTH } }}
                disabled={config.position === 'hidden'}
            >
                <InputLabel id="stats-style-label">Style</InputLabel>
                <Select
                    labelId="stats-style-label"
                    value={config.style}
                    label="Style"
                    onChange={(e) => onChange({ style: e.target.value })}
                    renderValue={(value) => STYLE_LABEL[value] || value}
                    MenuProps={{
                        PaperProps: { sx: { maxHeight: 560, maxWidth: 420 } },
                    }}
                    sx={{
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        ...accentBorderSx(accent),
                    }}
                >
                    {STATS_STYLE_GROUPS.flatMap((group) => [
                        <ListSubheader
                            key={`hdr-${group.label}`}
                            sx={{
                                lineHeight: '28px',
                                fontWeight: 800,
                                color: PALETTE.text,
                                backgroundColor: PALETTE.page,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1.2,
                            }}
                        >
                            {group.label}
                        </ListSubheader>,
                        ...group.styles.map((s) => (
                            <MenuItem key={s.value} value={s.value} sx={{ py: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Box sx={{ width: 100, flexShrink: 0, fontWeight: 600 }}>{s.label}</Box>
                                    <StatsPreview style={s.value} accent={accent} />
                                </Box>
                            </MenuItem>
                        )),
                    ])}
                </Select>
            </FormControl>
        </Box>
    );
};

// ============================================================================
// CAPTURE REGION
// ============================================================================

// The map is an 800-unit-wide SVG that scales to its container. The stats
// pill is HTML, so without help it would stay a fixed pixel size while the
// map shrinks on mobile — making the pill look huge. To keep the pill a
// CONSTANT proportion of the map at every screen size, we measure the
// rendered width and scale the pill by (renderedWidth / 800), exactly how
// the map's own SVG scales. Pill vertical padding, in 800-canvas units.
// This is the ONLY pill-to-map spacing knob. If a particular map's pill
// looks too far from its geography, the fix belongs in that map's SVG
// dimensions (so the geography fills the canvas), NOT in this file —
// MapStats is generic and must stay map-agnostic.
const PILL_STRIP_PADDING = 16;

export const MapCaptureRegion = React.forwardRef(({
    children,
    statsConfig,
    visited,
    total,
    accent,
    sx,
    innerRef,
}, ref) => {
    const position = statsConfig?.position || 'above';
    const style = statsConfig?.style || 'bold';
    const label = statsConfig?.label || 'visited';
    const visible = position !== 'hidden';

    // scale = renderedWidth / 800 (never above 1 — the pill is designed for
    // the 800-wide canvas and only ever scales down).
    const [scale, setScale] = useState(1);
    // natural (untransformed) pill height — transform: scale() does not change
    // offsetHeight, so this stays the design-size height.
    const [pillHeight, setPillHeight] = useState(56);

    const outerObserver = useRef(null);
    const pillObserver = useRef(null);

    // Callback ref for the outer box: forward to parent ref AND observe width.
    const setOuterRef = useCallback((node) => {
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;

        if (outerObserver.current) {
            outerObserver.current.disconnect();
            outerObserver.current = null;
        }
        if (node && typeof ResizeObserver !== 'undefined') {
            const update = () => {
                const w = node.offsetWidth || 800;
                setScale(Math.min(1, w / 800));
            };
            update();
            outerObserver.current = new ResizeObserver(update);
            outerObserver.current.observe(node);
        }
    }, [ref]);

    // Callback ref for the pill: measure its natural height (re-runs whenever
    // the pill node changes, e.g. when the style switches).
    const setPillRef = useCallback((node) => {
        if (pillObserver.current) {
            pillObserver.current.disconnect();
            pillObserver.current = null;
        }
        if (node && typeof ResizeObserver !== 'undefined') {
            const update = () => setPillHeight(node.offsetHeight || 56);
            update();
            pillObserver.current = new ResizeObserver(update);
            pillObserver.current.observe(node);
        }
    }, []);

    // NOTE: no background colour on the capture region or the stats strip —
    // toPng on a transparent element produces a PNG with a real alpha
    // channel, which is what makes the downloaded file printable on dark
    // products. The pill itself still has its own filled chrome.
    const strip = visible ? (
        <Box sx={{
            height: (pillHeight + PILL_STRIP_PADDING) * scale,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
        }}>
            <Box
                ref={setPillRef}
                sx={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
            >
                <StatsPill style={style} accent={accent} visited={visited} total={total} label={label} />
            </Box>
        </Box>
    ) : null;

    return (
        <Box
            ref={setOuterRef}
            sx={{
                width: '100%',
                maxWidth: '800px',
                ...sx
            }}
        >
            {position === 'above' && strip}
            <Box
                ref={innerRef}
                sx={{
                    position: 'relative',
                    // Stroke fallback for libraries (e.g. react-usa-map) that
                    // render bare <path> elements with no stroke. Scoped to
                    // the map area only — NOT applied to paths inside the
                    // stats pill, which have their own attribute-based
                    // strokes (gauge arcs, ring widths, etc.) that this rule
                    // would otherwise override.
                    //
                    // react-simple-maps' Geography renders inline `style="…"`
                    // on each path, which beats this CSS, so country/state
                    // maps still drive their own per-region styles.
                    '& path': {
                        stroke: GEOGRAPHY_STROKE,
                        strokeWidth: GEOGRAPHY_STROKE_WIDTH,
                    },
                }}
            >
                {children}
            </Box>
            {position === 'below' && strip}
        </Box>
    );
});

// ============================================================================
// CAPTURE HELPER
// ============================================================================

export const captureRegionAsBlob = async (element) => {
    if (!element) throw new Error('Capture element not available');
    const naturalWidth = element.offsetWidth || 800;
    const pixelRatio = Math.max(1, 800 / naturalWidth);
    // Omit backgroundColor so the exported PNG keeps the alpha channel — the
    // ocean / area outside the visited states is transparent, which prints
    // cleanly on dark cups, t-shirts and hoodies (no white halo around the
    // states).
    // skipFonts: true bypasses html-to-image's webfont-inlining step. Chrome
    // blocks `cssRules` reads on the cross-origin Google Fonts stylesheet,
    // which used to spam the console with SecurityError every capture. The
    // captured map uses system fonts where present and falls back to the
    // browser's already-loaded webfont rendering otherwise — no visible
    // change in the exported PNG.

    // html-to-image inlines per-element styles but does NOT inline styles
    // that come from a parent CSS class (e.g. our `& path { stroke: … }`
    // rule). Without this step, the captured PNG drops state borders even
    // though the live DOM shows them — verified by sampling adjacent
    // visited/not-visited pixels in the output and finding zero stroke
    // pixels between fills. Copy computed stroke + strokeWidth onto each
    // path's inline `style` so the capture pipeline picks them up.
    //
    // ONLY inline for paths that don't already define their own stroke.
    // Paths with inline `style="stroke:…"` (react-simple-maps Geography) or
    // an SVG attribute `stroke="…"` (StatsPill gauge arcs, marker outlines)
    // already render correctly and must not be touched — overriding them
    // would repaint the stats pill in the geography colour.
    const paths = element.querySelectorAll('svg path');
    const previousInline = new Map();
    paths.forEach((p) => {
        const hasInlineStroke = p.style && p.style.stroke && p.style.stroke !== '';
        const hasAttrStroke = p.hasAttribute('stroke');
        if (hasInlineStroke || hasAttrStroke) return;
        const cs = window.getComputedStyle(p);
        const stroke = cs.stroke;
        const strokeWidth = cs.strokeWidth;
        if (!stroke || stroke === 'none' || !strokeWidth) return;
        previousInline.set(p, p.getAttribute('style'));
        const existing = p.getAttribute('style') || '';
        p.setAttribute(
            'style',
            `${existing}; stroke: ${stroke}; stroke-width: ${strokeWidth};`,
        );
    });

    try {
        const dataUrl = await toPng(element, {
            pixelRatio,
            skipFonts: true,
            style: { transform: 'scale(1)', transformOrigin: 'top left' }
        });
        const response = await fetch(dataUrl);
        return await response.blob();
    } finally {
        previousInline.forEach((style, p) => {
            if (style === null) p.removeAttribute('style');
            else p.setAttribute('style', style);
        });
    }
};
